import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthProvider";
import FeedbackWidgetWithCaptcha from "./FeedbackWidget/FeedbackWidgetWithCaptcha";

export const FloatingFeedbackButton: React.FC = () => {
  const { user } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if viewport is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load hidden state from localStorage
  useEffect(() => {
    const hidden = localStorage.getItem("vitly-feedback-widget-hidden");
    if (hidden === "true") {
      setIsHidden(true);
    }

    // Listen for storage changes from UserDropdown
    const handleStorageChange = () => {
      const hidden = localStorage.getItem("vitly-feedback-widget-hidden");
      setIsHidden(hidden === "true");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Handle dismissing the widget
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsHidden(true);
    localStorage.setItem("vitly-feedback-widget-hidden", "true");
  };

  if (!user || isHidden) return null;

  return (
    <>
      {/* Floating Feedback Button with Hide Option */}
      <div
        data-feedback-button="true"
        className={`fixed ${isMobile ? "bottom-20 right-4" : "bottom-24 right-6"} z-50 md:bottom-6 md:right-6 group/container`}
      >
        {/* Dismiss Button - Desktop only (appears on hover) */}
        {/* Mobile users can hide via Settings menu → "Show Feedback Widget" toggle */}
        <button
          onClick={handleDismiss}
          className="absolute -top-3 -right-3
            w-5 h-5
            bg-gray-800 dark:bg-gray-200
            text-white dark:text-gray-800 rounded-full shadow-lg
            hidden md:flex items-center justify-center
            hover:bg-red-600 dark:hover:bg-red-500 hover:text-white
            transition-all duration-200 z-10
            opacity-0 group-hover/container:opacity-100
            active:scale-90"
          aria-label="Hide feedback button"
          title="Hide feedback button"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Main Feedback Button */}
        <button
          onClick={() => setIsFeedbackOpen(!isFeedbackOpen)}
          className={`
            relative ${isMobile ? "w-14 h-14" : "w-16 h-16"} rounded-full
            bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500
            hover:from-purple-700 hover:via-purple-600 hover:to-pink-600
            text-white shadow-xl shadow-purple-500/50
            flex items-center justify-center
            transition-all duration-300 ease-out
            hover:shadow-2xl hover:scale-110
            active:scale-95 active:shadow-lg
            group
            ${isFeedbackOpen ? "rotate-[360deg] scale-110" : ""}
          `}
          style={{
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}
          aria-label={isFeedbackOpen ? "Close feedback" : "Send feedback"}
          title={isFeedbackOpen ? "Close feedback" : "Send Feedback"}
        >
          {/* Icon with rotation animation */}
          {isFeedbackOpen ? (
            // X icon when open
            <svg
              className="w-6 h-6 absolute inset-0 m-auto transition-all duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            // Message icon when closed
            <svg
              className="w-6 h-6 absolute inset-0 m-auto transition-all duration-300 group-hover:scale-110"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          )}

          {/* Animated ring effect - only show when not open */}
          {!isFeedbackOpen && (
            <span className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping opacity-20"></span>
          )}

          {/* Active indicator when form is open */}
          {isFeedbackOpen && (
            <span className="absolute inset-0 rounded-full border-4 border-white/50 animate-pulse"></span>
          )}
        </button>
      </div>

      {/* Feedback Widget Modal */}
      <FeedbackWidgetWithCaptcha
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </>
  );
};

export default FloatingFeedbackButton;
